import {
    DownloadOutlined,
    FolderOutlined,
    LeftOutlined,
} from '@ant-design/icons';
import {
    Button,
    Image,
    message,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
} from 'antd';
import {useEffect, useState} from 'react';
import axiosApiInstance from '../../../api/axiosClient';
import {fetchReleaseFiles, UploadedFile} from './fileUtils';

export const formatFileSize = (size: number) => {
    const kb = size / 1024;
    if (kb < 1024) {
        return `${kb.toFixed(2)} KB`;
    }
    return `${(kb / 1024).toFixed(2)} MB`;
};

export const formatFileDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const downloadReleaseFile = async (folderName: string, fileName: string) => {
    const {data} = await axiosApiInstance.post<{presignedUrl: string}>(
        '/api/products/releases/generate-presigned-url-get',
        {fileName, folderName, download: true},
    );
    const link = document.createElement('a');
    link.href = data.presignedUrl;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
};

const DDEX_PREP_MESSAGE_KEY = 'ddex-package-preparing';

export const clearDdexPreparingMessage = () => {
    message.destroy(DDEX_PREP_MESSAGE_KEY);
};

export const downloadReleaseDDEX = async (releaseId: string, upc: string) => {
    const response = await axiosApiInstance.get<Blob>(
        `/api/products/releases/${releaseId}/ddex-package`,
        {responseType: 'blob', skipErrorNotification: true},
    );
    clearDdexPreparingMessage();
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${upc || releaseId}.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const ReleaseFolderGrid = ({
    releases,
    loading,
    onOpen,
    labelKey = 'upc_ean',
}: {
    releases: API.MusicDistribution.MusicRelease[];
    loading: boolean;
    onOpen: (release: API.MusicDistribution.MusicRelease) => void;
    /** @deprecated Prefer default grid showing UPC + title; kept for callers that only want one line. */
    labelKey?: 'upc_ean' | 'name';
}) => {
    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: 48}}>
                <Spin/>
            </div>
        );
    }
    if (releases.length === 0) {
        return (
            <div style={{textAlign: 'center', padding: 48, color: '#aaa'}}>
                No releases
            </div>
        );
    }
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 8,
                padding: 16,
            }}
        >
            {releases.map((release) => {
                const upc = (release.upc_ean || '').trim() || release.id || '—';
                const title = (release.name || '').trim();
                const tooltip =
                    title && title !== upc ? `${upc} — ${title}` : upc;
                const legacySingleLine =
                    labelKey === 'name' ? title || upc : upc;

                return (
                    <Tooltip key={release.id || upc} title={tooltip}>
                        <Button
                            type="text"
                            onClick={() => onOpen(release)}
                            style={{
                                height: 'auto',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 4,
                                padding: '12px 8px',
                            }}
                        >
                            <FolderOutlined style={{fontSize: 40, color: '#e8b931'}}/>
                            {labelKey === 'upc_ean' || labelKey === undefined ? (
                                <>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            lineHeight: 1.3,
                                            fontWeight: 600,
                                            wordBreak: 'break-all',
                                            textAlign: 'center',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {upc}
                                    </span>
                                    {title ? (
                                        <span
                                            style={{
                                                fontSize: 11,
                                                lineHeight: 1.25,
                                                color: 'var(--text-secondary)',
                                                wordBreak: 'break-word',
                                                textAlign: 'center',
                                                maxWidth: '100%',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {title}
                                        </span>
                                    ) : null}
                                </>
                            ) : (
                                <span
                                    style={{
                                        fontSize: 12,
                                        lineHeight: 1.3,
                                        wordBreak: 'break-all',
                                        textAlign: 'center',
                                    }}
                                >
                                    {legacySingleLine}
                                </span>
                            )}
                        </Button>
                    </Tooltip>
                );
            })}
        </div>
    );
};

export type ClientFolderItem = {
    key: string;
    label: string;
    releaseCount?: number;
};

export const truncateFolderLabel = (text: string, maxLength = 20) => {
    if (text.length <= maxLength) {
        return text;
    }
    return `${text.slice(0, maxLength)}…`;
};

export const ClientFolderGrid = ({
    folders,
    loading,
    onOpen,
    minColumnWidth = 150,
    gap = 12,
    labelMaxLength = 30,
}: {
    folders: ClientFolderItem[];
    loading: boolean;
    onOpen: (key: string) => void;
    minColumnWidth?: number;
    gap?: number;
    labelMaxLength?: number;
}) => {
    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: 48}}>
                <Spin/>
            </div>
        );
    }
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}px, 1fr))`,
                gap,
                padding: 16,
            }}
        >
            {folders.map((folder) => {
                const countLabel =
                    folder.releaseCount != null
                        ? `${folder.releaseCount} release${folder.releaseCount === 1 ? '' : 's'}`
                        : null;
                const displayLabel = truncateFolderLabel(folder.label, labelMaxLength);
                const tooltip = countLabel ? `${folder.label} · ${countLabel}` : folder.label;
                return (
                    <Tooltip key={folder.key} title={tooltip}>
                        <Button
                            type="text"
                            onClick={() => onOpen(folder.key)}
                            style={{
                                height: 'auto',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 4,
                                padding: '12px 8px',
                            }}
                        >
                            <FolderOutlined style={{fontSize: 40, color: '#e8b931'}}/>
                            <span
                                style={{
                                    fontSize: 12,
                                    lineHeight: 1.3,
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {displayLabel}
                            </span>
                            {countLabel ? (
                                <span style={{fontSize: 11, lineHeight: 1.2, color: 'var(--text-muted)'}}>
                                    {countLabel}
                                </span>
                            ) : null}
                        </Button>
                    </Tooltip>
                );
            })}
        </div>
    );
};

export const ReleaseFolderFiles = ({
    release,
    onBack,
    backLabel = 'Back to folders',
    hideDdexDownload = false,
}: {
    release: API.MusicDistribution.MusicRelease;
    onBack: () => void;
    backLabel?: string;
    hideDdexDownload?: boolean;
}) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState<string | null>(null);
    const [downloadingDdex, setDownloadingDdex] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchReleaseFiles(release.id, (next) => {
            if (!cancelled) setFiles(next);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [release.id]);

    const onDownload = async (file: UploadedFile) => {
        setDownloading(file.uid);
        try {
            await downloadReleaseFile(release.id, file.name);
        } catch (error) {
            console.error(error);
            message.error(`Failed to download ${file.name}`);
        } finally {
            setDownloading(null);
        }
    };

    const onDownloadDdex = async () => {
        setDownloadingDdex(true);
        message.open({
            type: 'loading',
            content: `Preparing DDEX package for ${release.upc_ean}...`,
            key: DDEX_PREP_MESSAGE_KEY,
            duration: 0,
        });
        try {
            await downloadReleaseDDEX(release.id, release.upc_ean);
            message.success('DDEX package downloaded');
        } catch (error) {
            console.error(error);
            message.error('Failed to download DDEX package');
        } finally {
            clearDdexPreparingMessage();
            setDownloadingDdex(false);
        }
    };

    return (
        <div style={{padding: 16}}>
            {!hideDdexDownload ? (
                <Button onClick={onBack} icon={<LeftOutlined/>} style={{marginBottom: 16}}>
                    {backLabel}
                </Button>
            ) : null}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16}}>
                <div>
                    <div style={{fontSize: 18, fontWeight: 600}}>{release.upc_ean}</div>
                    {release.name ? <div style={{color: 'var(--text-muted)'}}>{release.name}</div> : null}
                </div>
                {!hideDdexDownload ? (
                    <Button
                        type="primary"
                        icon={<DownloadOutlined/>}
                        loading={downloadingDdex}
                        disabled={!release.upc_ean}
                        onClick={onDownloadDdex}
                    >
                        Download DDEX
                    </Button>
                ) : null}
            </div>
            <Table<UploadedFile>
                rowKey="uid"
                loading={loading}
                dataSource={files}
                pagination={false}
                locale={{emptyText: loading ? 'Loading files...' : 'No uploaded files'}}
                columns={[
                    {
                        title: 'File',
                        dataIndex: 'name',
                        render: (name: string, file) => (
                            <Space>
                                {file.fileType === 'image' && file.url ? (
                                    <Image src={file.url} width={36} height={36} style={{objectFit: 'cover'}}/>
                                ) : null}
                                <span>{name}</span>
                            </Space>
                        ),
                    },
                    {
                        title: 'Type',
                        dataIndex: 'fileType',
                        width: 110,
                        render: (type: string) => {
                            const color = type === 'image' ? 'blue' : type === 'audio' ? 'green' : 'default';
                            return <Tag color={color}>{(type || 'other').toUpperCase()}</Tag>;
                        },
                    },
                    {
                        title: 'Size',
                        dataIndex: 'size',
                        width: 120,
                        render: (size: number) => formatFileSize(size || 0),
                    },
                    {
                        title: 'Duration',
                        dataIndex: 'duration',
                        width: 110,
                        render: (duration: number, file) =>
                            file.fileType === 'audio' ? formatFileDuration(duration) : '-',
                    },
                    {
                        title: '',
                        width: 130,
                        render: (_value, file) => (
                            <Button
                                type="link"
                                icon={<DownloadOutlined/>}
                                loading={downloading === file.uid}
                                onClick={() => onDownload(file)}
                            >
                                Download
                            </Button>
                        ),
                    },
                ]}
            />
        </div>
    );
};
